import { DocumentsOnTemplateView } from 'presentation/document/documentsOnTemplateView';
import React, {
  PropsWithChildren,
  ReactElement,
  useEffect,
  useMemo,
} from 'react';
import { useProject } from 'store/project';
import { useLocation } from 'react-router-dom';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { useProcessesObject } from 'store/process';
import { isEditPath } from 'setup/navigation/routeMatchers';
import { APP_PATHS } from 'setup/navigation/paths';
import { useRedirectContext } from '../../common/withRedirect';
import {
  useInspectionTemplate,
  useInspectionTemplateInitializer,
} from 'store/inspection';
import { toInspectionTemplateItemModel } from './model';
import { InspectionTemplateItemModel } from './types';

interface RequiredTemplateContextType {
  template: InspectionTemplateItemModel | undefined;
  inspectionTemplateDocuments: DocumentsOnTemplateView;
  loading: boolean;
}

const RequiredInspectionTemplateContext = React.createContext<
  RequiredTemplateContextType | undefined
>(undefined);

interface TemplateContainer {
  template?: InspectionTemplateItemModel;
  templateId?: string;
}

/**
 * HOC for wizard/create flows where template comes from URL params or navigation state.
 * For single inspection view, use `useInspectionTemplateOnView` hook directly instead.
 */
const WithRequiredInspectionTemplateInner: React.FC<
  PropsWithChildren<{ onTemplateFailed?: () => void }>
> = ({ children, onTemplateFailed }) => {
  const location = useLocation();
  const { organizationId, _id: projectId } = useProject();
  const stateTemplate = (location.state as TemplateContainer)?.template;
  const stateTemplateId = (location.state as TemplateContainer)?.templateId;
  const paramsTemplateId = useAppUrlParams(
    UrlParamName.INSPECTION_TEMPLATE_ID
  );

  const { redirect, setCanRedirect, setRedirection } = useRedirectContext();
  const processes = useProcessesObject();

  const templateId = stateTemplateId || paramsTemplateId || stateTemplate?._id;

  useInspectionTemplateInitializer(templateId);
  const {
    inspectionTemplate,
    inspectionTemplateDocuments,
    loading,
  } = useInspectionTemplate();

  const template = useMemo(() => {
    if (!inspectionTemplate) return stateTemplate;
    
    return toInspectionTemplateItemModel(
      processes,
      inspectionTemplate,
      organizationId
    );
  }, [inspectionTemplate, processes, organizationId, stateTemplate]);

  useEffect(() => {
    if (!stateTemplate && !templateId && !isEditPath(location.pathname)) {
      if (onTemplateFailed) {
        onTemplateFailed();
      } else {
        setCanRedirect(true);
        setRedirection({
          path: APP_PATHS.inspectionTemplate.root(projectId),
          state: undefined,
        });
        redirect();
      }
    }
  }, [
    stateTemplate,
    templateId,
    onTemplateFailed,
    setCanRedirect,
    setRedirection,
    redirect,
    location.pathname,
    projectId,
  ]);

  const ctx = { template, inspectionTemplateDocuments, loading };

  return (
    <RequiredInspectionTemplateContext.Provider value={ctx}>
      {children}
    </RequiredInspectionTemplateContext.Provider>
  );
};

export function useRequiredInspectionTemplate(): RequiredTemplateContextType {
  const context = React.useContext(RequiredInspectionTemplateContext);
  if (context === undefined) {
    throw new Error(
      'useRequiredInspectionTemplate must be used within a RequiredInspectionTemplateContextProvider'
    );
  }
  return context;
}

export const withRequiredInspectionTemplate =
  (Component: React.ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithRequiredInspectionTemplateInner>
      <Component {...props} />
    </WithRequiredInspectionTemplateInner>
  );
